import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { getCookie, getReferrer } from '../shared/constants/app-enums';

@Injectable({ providedIn: 'root' })
export class PixelService {
    // Session management properties
    private sessionId: string = '';
    private sessionStartTime: number = 0;
    private pageViewStartTime: number = 0;
    private currentPageName: string = '';
    private pageViewCount: number = 0;
    private hasPurchased: boolean = false;
    private currentPageViewId: string = '';

    private _pixelUUID: string = '';
    public consentStatus = false;
    private eventQueue: { eventType: string, eventData: any }[] = [];

    private readonly http = inject(HttpClient);

    /**
     * Getter for pixelUUID
     */
    get pixelUUID(): string {
        return this._pixelUUID;
    }

    /**
     * Setter for pixelUUID - initializes session ID when set
     */
    set pixelUUID(value: string) {
        if (!value || value === 'undefined' || value === 'null') {
            return;
        }

        this._pixelUUID = value;
        // Initialize session ID if not already set
        if (!this.sessionId && value) {
            this.initializeSessionId();
        }

        // Process queued events
        if (value && this.eventQueue.length > 0) {
            this.processEventQueue();
        }
    }

    constructor() {
        this.initializeSession();
        // Check for existing consent cookie
        if (typeof window !== 'undefined') {
            const consentUserId = getCookie('consent_user_id');
            if (consentUserId && consentUserId !== 'undefined' && consentUserId !== 'null') {
                this.pixelUUID = consentUserId;
            }
        }
    }

    /**
     * Process queued events once pixelUUID is available
     */
    private processEventQueue(): void {
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            if (event) {
                this.sendPixelEvent(event.eventType, event.eventData);
            }
        }
    }

    /**
     * Initialize session tracking
     */
    private initializeSession(): void {
        if (typeof window === 'undefined') return;

        this.sessionStartTime = Date.now();
        this.pageViewStartTime = Date.now();
        this.pageViewCount = 0;
        this.hasPurchased = false;

        // Listen for page unload to send session end event
        window.addEventListener('beforeunload', () => {
            this.endSession();
        });
    }

    /**
     * Initialize session ID after pixelUUID is set
     * Should be called once pixelUUID is available
     */
    initializeSessionId(): void {
        if (!this.sessionId && this.pixelUUID) {
            this.sessionId = this.generateSessionId();
        }
    }

    /**
     * Generate a unique session ID
     * Format: ss_{{userConsentId}}_{{timestamp}}
     * Only generates if pixelUUID is set
     */
    private generateSessionId(): string {
        if (!this.pixelUUID) {
            return '';
        }
        const timestamp = Date.now();
        return `ss_${this.pixelUUID}_${timestamp}`;
    }

    /**
     * Generate a unique transaction ID
     * Format: tx_{{userConsentId}}_{{timestamp}}
     * Only generates if pixelUUID is set
     */
    generateTransactionId(): string {
        if (!this.pixelUUID) {
            console.warn('Transaction ID requested before pixelUUID is set');
            return '';
        }
        const timestamp = Date.now();
        return `tx_${this.pixelUUID}_${timestamp}`;
    }

    /**
     * Generate a unique topup ID
     * Format: tp_{{userConsentId}}_{{timestamp}}
     * Only generates if pixelUUID is set
     */
    generateTopupId(): string {
        if (!this.pixelUUID) {
            console.warn('Topup ID requested before pixelUUID is set');
            return '';
        }
        const timestamp = Date.now();
        return `tp_${this.pixelUUID}_${timestamp}`;
    }

    /**
     * Generate a unique page view ID
     * Format: pv_{{userConsentId}}_{{timestamp}}
     * Only generates if pixelUUID is set
     */
    generatePageViewId(): string {
        if (!this.pixelUUID) {
            // console.warn('Page View ID requested before pixelUUID is set');
            return '';
        }
        const timestamp = Date.now();
        return `pv_${this.pixelUUID}_${timestamp}`;
    }

    /**
     * Get device type (desktop/mobile/tablet)
     */
    private getDeviceType(): string {
        if (typeof window === 'undefined') return 'unknown';
        const ua = navigator.userAgent;
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return 'tablet';
        }
        if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
            return 'mobile';
        }
        return 'desktop';
    }

    /**
     * Get browser name
     */
    private getBrowser(): string {
        if (typeof window === 'undefined') return 'unknown';
        const ua = navigator.userAgent;
        if (ua.indexOf('Brave') > -1 || (navigator as any).brave) return 'Brave';
        if (ua.indexOf('Firefox') > -1) return 'Firefox';
        if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) return 'Opera';
        if (ua.indexOf('Trident') > -1) return 'IE';
        if (ua.indexOf('Edge') > -1) return 'Edge';
        if (ua.indexOf('Chrome') > -1) return 'Chrome';
        if (ua.indexOf('Safari') > -1) return 'Safari';
        return 'unknown';
    }

    /**
     * Get operating system
     */
    private getOS(): string {
        if (typeof window === 'undefined') return 'unknown';
        const ua = navigator.userAgent;
        if (ua.indexOf('Win') > -1) return 'Windows';
        if (ua.indexOf('Mac') > -1) return 'MacOS';
        if (ua.indexOf('Linux') > -1) return 'Linux';
        if (ua.indexOf('Android') > -1) return 'Android';
        if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) return 'iOS';
        return 'unknown';
    }

    /**
     * Get UTM parameters from URL
     */
    private getUTMParams(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
        if (typeof window === 'undefined') return {};
        const params = new URLSearchParams(window.location.search);
        return {
            utm_source: params.get('utm_source') || undefined,
            utm_medium: params.get('utm_medium') || undefined,
            utm_campaign: params.get('utm_campaign') || undefined,
        };
    }

    /**
     * Unified pixel event tracking method
     * Send any pixel event with flexible data structure
     * @param eventType - The type of event (e.g., 'session_event', 'buy_event', 'wallet_connect_event')
     * @param eventData - The event data object containing all relevant properties
     */
    async sendPixelEvent(eventType: string, eventData: any): Promise<void> {
        if (environment.production) {
            if (!this.pixelUUID || this.pixelUUID === 'undefined' || this.pixelUUID === 'null') {
                // Queue event if pixelUUID is not yet available
                this.eventQueue.push({ eventType, eventData });
                // console.log('Pixel event queued (waiting for UUID):', eventType);
                return;
            }

            if (typeof window === 'undefined') {
                return;
            }

            const timestamp = new Date().toISOString();
            if (eventData.wallet_name) {
                eventData.wallet_name = eventData.wallet_name == 'ncw' ? 'Best Web Wallet' : eventData.wallet_name;
            }

            // Ensure session ID is available, if not try to init
            if (!this.sessionId) {
                this.initializeSessionId();
            }

            // if (eventData.chain_id == environment.sol.chainIdInt) {
            //     eventData.chain_id = 50101;
            // }

            // Build final payload with common params at top level
            const payload = {
                user_consent_id: this.pixelUUID,
                event_type: eventType,
                timestamp,
                page_url: window.location.href,
                session_id: this.sessionId,
                project_slug: environment.slug,
                properties: {
                    ...eventData,
                    referrer_code: getReferrer(),
                },
            };

            // this.http.post(environment.crmPixelEventsApiUrl, payload).subscribe(
            //     (res: any) => {
            //         console.log('Pixel event sent successfully:', eventType, res);
            //     },
            //     (err: any) => {
            //         console.error('Pixel event error:', eventType, err);
            //     }
            // );
        }
    }

    /**
     * End session and send session event
     */
    private endSession(): void {
        const now = Date.now();
        const duration = Math.floor((now - this.sessionStartTime) / 1000);
        const utmParams = this.getUTMParams();

        const sessionData = {
            action: 'end',
            referrer_code: getReferrer(),
            utm_source: utmParams.utm_source,
            utm_medium: utmParams.utm_medium,
            utm_campaign: utmParams.utm_campaign,
            device_type: this.getDeviceType(),
            browser: this.getBrowser(),
            os: this.getOS(),
            start_time: new Date(this.sessionStartTime).toISOString(),
            end_time: new Date(now).toISOString(),
            duration_sec: duration,
            session_type: this.hasPurchased ? 'buyer' : 'presale_visit',
            page_views: this.pageViewCount,
        };

        this.sendPixelEvent('session_event', sessionData);
    }

    /**
* start session and send session event
*/
    startSession(): void {
        const utmParams = this.getUTMParams();

        const sessionData = {
            action: 'start',
            referrer_code: getReferrer(),
            utm_source: utmParams.utm_source,
            utm_medium: utmParams.utm_medium,
            utm_campaign: utmParams.utm_campaign,
            device_type: this.getDeviceType(),
            browser: this.getBrowser(),
            os: this.getOS(),
            start_time: new Date(this.sessionStartTime).toISOString(),
            end_time: 0,
            duration_sec: 0,
            session_type: this.hasPurchased ? 'buyer' : 'presale_visit',
            page_views: 0,
        };

        this.sendPixelEvent('session_event', sessionData);
    }

    /**
     * Track page view
     */
    trackPageView(pageName: string): void {
        // Send event for previous page if exists (EXIT event)
        if (this.currentPageName && this.pageViewStartTime > 0) {
            const timeSpent = Math.floor((Date.now() - this.pageViewStartTime) / 1000);

            const pageViewData = {
                page_name: this.currentPageName,
                time_spent_sec: timeSpent,
                pageview_event_id: this.currentPageViewId
            };

            this.sendPixelEvent('page_view_event', pageViewData);
        }

        // Update for new page
        this.currentPageName = pageName;
        this.pageViewStartTime = Date.now();
        this.pageViewCount++;
        this.currentPageViewId = this.generatePageViewId();

        // Send event for new page (ENTRY event)
        const entryPageViewData = {
            page_name: this.currentPageName,
            time_spent_sec: 0,
            pageview_event_id: this.currentPageViewId
        };
        this.sendPixelEvent('page_view_event', entryPageViewData);
    }

    /**
     * Mark current session as a buyer session
     */
    markSessionAsBuyer(): void {
        this.hasPurchased = true;
    }

    /**
     * Track external link clicks
     * @param extUrl - The external URL being clicked
     * @param linkName - Name/description of the link (e.g., 'telegram', 'twitter', 'whitepaper')
     */
    trackPageClick(extUrl: string, linkName: string): void {
        const timeSpent = Math.floor((Date.now() - this.pageViewStartTime) / 1000);

        // Determine page name based on URL
        let pageName = this.currentPageName;
        if (!pageName && typeof window !== 'undefined') {
            const pathname = window.location.pathname;
            // Check if it's staking page
            if (pathname.includes('/staking')) {
                pageName = 'staking';
            } else if (pathname === '/' || pathname.match(/^\/[a-z]{2}\/?$/)) {
                // Base URL or language-prefixed base URL (e.g., '/', '/en', '/es/')
                pageName = 'home';
            } else {
                pageName = pathname;
            }
        }

        const clickData = {
            page_name: pageName || 'unknown',
            ext_url: extUrl,
            link_name: linkName,
            time_spent_sec: timeSpent,
        };

        this.sendPixelEvent('page_click_event', clickData);
    }
}
